import { Messages } from 'primereact/messages';
import { RadioButton } from 'primereact/radiobutton';
import { classNames } from 'primereact/utils';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ButtonAdd, ButtonEdit } from '../../../components/Buttons';
import { FormContainer, FormInput, FormRow, Label } from '../../../components/form';
import { getFormErrorMessage, maxLengthMessage } from '../../../utils/errors';
import { RHFInputText, RHFDropdown, RHFInputNumber, RHFInputTextarea } from '../../../components/rhf';
import { InputText } from '../../../../components/PrimereactComponents';
import { ModalDialog } from '../../../components/ModalDialog';
import { ORGAN } from '../../../constants/field_lengths';

export const BodyDialog = forwardRef(({ isVisible, municipality, editBody, bodyTypeOptions, onHide, onSubmitAdd, onSubmitEdit, msgs }, ref) => {
    const [processing, setProcessing] = useState(false);

    useImperativeHandle(ref, () => ({
        reset: reset
    }));

    const formId = 'add-body-dialog';

    const MIN_POCET_ZASTUPITELU = 5;
    const MAX_POCET_ZASTUPITELU = 70;

    const edit = !!editBody;
    const onSubmit = edit ? onSubmitEdit : onSubmitAdd;

    const defaultValues = useMemo(
        () => ({
            body_type: null,
            body_name: '',
            body_name_genitive: '',
            body_member_count: MIN_POCET_ZASTUPITELU,
            body_place: '',
            body_application_name: '',
            body_application_description: '',
            body_data_url: ''
        }), []
    );

    const municipality_name = municipality ? municipality.name : '';
    const body_type_options = municipality ? bodyTypeOptions.filter(option => !new Set(municipality.bodies.map(body => body.type_text_id)).has(option.text_value)) : [];
    

    const {
        control,
        formState: { errors, isSubmitting },
        handleSubmit,
        reset
    } = useForm({
        disabled: processing,
        defaultValues: defaultValues
    });

    useEffect(() => {
        setProcessing(isSubmitting);
    }, [isSubmitting]);

    useEffect(() => {
        if (editBody) {
            reset({
                body_name: editBody.name,
                body_name_genitive: editBody.name_genitive,
                body_member_count: editBody.member_count,
                body_place: editBody.place,
                body_application_name: editBody.application_name,
                body_application_description: editBody.application_description,
                body_data_url: editBody.data_url
            });
        } else {
            reset(defaultValues);
        }
    }, [editBody, reset, defaultValues]);

    return (
        <ModalDialog
            visible={isVisible}
            header={edit ? `Upravit orgán` : `Přidat orgán pro municipalitu ${municipality_name}`}
            style={{ width: 650 }}
            footer={
                edit ? (
                    <ButtonEdit label='Upravit' type='submit' form={formId} />
                ) : (
                    <ButtonAdd label='Přidat' type='submit' form={formId} />
                )
            }
            onHide={onHide}
        >
            <Messages ref={msgs} className='flex-center' />

            <form onSubmit={handleSubmit(onSubmit)} id={formId}>
                <FormContainer type='dialog'>
                    <FormRow>
                        <FormInput>
                            <Label htmlFor='body_type' text='Typ orgánu' required />
                            {edit ? (
                                <InputText name='body_type' disabled defaultValue={editBody.type_name} />
                            ) : (
                                <RHFDropdown
                                    name='body_type'
                                    control={control}
                                    rules={{ required: 'Vyplňte toto pole' }}
                                    placeholder='Zvolte typ orgánu'
                                    inputProps={{ options: body_type_options, showClear: false }} // TODO
                                />
                            )}
                            {getFormErrorMessage(errors, 'body_type')}
                        </FormInput>
                    </FormRow>
                    <FormRow>
                        <FormInput width='lg'>
                            <Label htmlFor='body_name' text='Název' required />
                            <RHFInputText
                                control={control}
                                name='body_name'
                                rules={{
                                    required: 'Vyplňte toto pole',
                                    maxLength: {
                                        value: ORGAN.NAZEV,
                                        message: maxLengthMessage(ORGAN.NAZEV)
                                    }
                                }}
                                placeholder='např. Zastupitelstvo města Brna'
                            />
                            {getFormErrorMessage(errors, 'body_name')}
                        </FormInput>
                        <FormInput width='lg'>
                            <Label htmlFor='body_name_genitive' text='Název (2. pád)' required />
                            <RHFInputText
                                control={control}
                                name='body_name_genitive'
                                rules={{
                                    required: 'Vyplňte toto pole',
                                    maxLength: {
                                        value: ORGAN.NAZEV_GENITIV,
                                        message: maxLengthMessage(ORGAN.NAZEV_GENITIV)
                                    }
                                }}
                                placeholder='např. Zastupitelstva města Brna'
                            />
                            {getFormErrorMessage(errors, 'body_name_genitive')}
                        </FormInput>
                    </FormRow>
                    <FormRow>
                        <FormInput width='lg'>
                            <Label htmlFor='body_application_name' text='Název aplikace' required />
                            <RHFInputText
                                control={control}
                                name='body_application_name'
                                rules={{
                                    required: 'Vyplňte toto pole',
                                    maxLength: {
                                        value: ORGAN.NAZEV_APLIKACE,
                                        message: maxLengthMessage(ORGAN.NAZEV_APLIKACE)
                                    }
                                }}
                                placeholder='např. Vizualizace hlasování Zastupitelstva města Brna'
                            />
                            {getFormErrorMessage(errors, 'body_application_name')}
                        </FormInput>
                        <FormInput>
                            <Label htmlFor='body_member_count' text='Počet zastupitelů' required />
                            <RHFInputNumber
                                control={control}
                                name='body_member_count'
                                rules={{
                                    required: 'Vyplňte toto pole',
                                    min: {
                                        value: MIN_POCET_ZASTUPITELU,
                                        message: `Minimální počet zastupitelů je ${MIN_POCET_ZASTUPITELU}`
                                    },
                                    max: {
                                        value: MAX_POCET_ZASTUPITELU,
                                        message: `Maximální počet zastupitelů je ${MAX_POCET_ZASTUPITELU}`
                                    }
                                }}
                            />
                            {getFormErrorMessage(errors, 'body_member_count')}
                        </FormInput>
                    </FormRow>
                    <FormRow>
                        <FormInput width='lg'>
                            <Label htmlFor='body_application_description' text='Popis aplikace' required />
                            <RHFInputTextarea
                                control={control}
                                name='body_application_description'
                                rules={{
                                    required: 'Vyplňte toto pole',
                                    maxLength: {
                                        value: ORGAN.POPIS_APLIKACE,
                                        message: maxLengthMessage(ORGAN.POPIS_APLIKACE)
                                    }
                                }}
                                placeholder='např. Přehledné vizualizace z 8. a 9. Zastupitelstva města Brna pro širokou veřejnost.'
                            />
                            {getFormErrorMessage(errors, 'body_application_description')}
                        </FormInput>
                        <FormInput width='lg'>
                            <Label htmlFor='body_place' text='Místo konání zasedání' />
                            <RHFInputText
                                control={control}
                                name='body_place'
                                rules={{
                                    maxLength: {
                                        value: ORGAN.MISTO,
                                        message: maxLengthMessage(ORGAN.MISTO)
                                    }
                                }}
                                placeholder='např. v zasedací síni Nové radnice'
                            />
                            {getFormErrorMessage(errors, 'body_place')}
                        </FormInput>
                        <FormInput width='lg'>
                            <Label htmlFor='body_data_url' text='Odkaz na zdroj hlasovacích dat' />
                            <RHFInputText
                                control={control}
                                name='body_data_url'
                                rules={{
                                    maxLength: {
                                        value: ORGAN.URL_ZDROJ_DAT,
                                        message: maxLengthMessage(ORGAN.URL_ZDROJ_DAT)
                                    }
                                }}
                                placeholder='např. https://www.brno.cz/zasedani-zmb'
                            />
                            {getFormErrorMessage(errors, 'body_data_url')}
                        </FormInput>
                    </FormRow>
                </FormContainer>
            </form>
        </ModalDialog>
    );
});
